import {Box, IconButton, Paper, Skeleton, Stack, Typography} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {keyframes} from "@emotion/react";
import * as React from "react";
import {useTranslation} from "react-i18next";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {comparisonIsGood, HeadlineValue, StatComparison, TrendChip} from "./Headline";
import {dimmedWhile, InsightsLoading} from "./insightsLoading";
import {InsightsTooltip, TooltipRow, TooltipTitle} from "./InsightsTooltip";

export interface StatTarget {
    label: string;
    progress: number;
    achieved: boolean;
}

export interface StatCardData {
    id: string;
    title: string;
    hint?: string;
    value: string;
    comparison?: StatComparison;
    target?: StatTarget;
}

export type TargetTone = "success" | "warning" | "error";

const WARNING_DISTANCE = 20;

/**
 * `achieved` is direction-aware wherever it is produced, so this also reads ceiling targets such as
 * cancellation rate, where staying under the figure is the win.
 */
export function targetProgressTone({progress, achieved}: Pick<StatTarget, "progress" | "achieved">): TargetTone {
    if (achieved) {
        return "success";
    }

    return Math.abs(100 - progress) <= WARNING_DISTANCE ? "warning" : "error";
}

const OVERSHOOT = 25;

const grow = keyframes`from {
                           width: 0;
                       }`;

interface SegmentProps {
    span: number;
    filled: number;
    tone: TargetTone;
}

function Segment({span, filled, tone}: SegmentProps) {
    return (
        <Box
            sx={theme => ({
                flexGrow: span,
                flexBasis: 0,
                position: "relative",
                height: "8px",
                borderRadius: 0.5,
                bgcolor: alpha(theme.palette[tone].main, 0.14),
                overflow: "hidden"
            })}
        >
            <Box
                key={filled}
                sx={theme => ({
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: `${filled}%`,
                    bgcolor: theme.palette[tone].main,
                    animation: `${grow} 650ms cubic-bezier(0.22, 1, 0.36, 1) 100ms both`,
                    "@media (prefers-reduced-motion: reduce)": {animation: "none"}
                })}
            />
        </Box>
    );
}

interface StatTitleProps {
    title: string;
    hint?: string;
}

function StatTitle({title, hint}: StatTitleProps) {
    return (
        <Box sx={{display: "flex", alignItems: "center", minHeight: "32px"}}>
            <Stack direction={"row"} alignItems={"center"} gap={0.25} sx={{minWidth: 0}}>
                <Typography variant={"body1"} color={"text.secondary"} fontWeight={600} noWrap>
                    {title}
                </Typography>

                {!!hint && (
                    <InsightsTooltip placement={"top"} title={hint}>
                        <IconButton
                            size={"small"}
                            aria-label={`${title} information`}
                            sx={{color: "text.disabled", flexShrink: 0, p: 0.5}}
                        >
                            <InfoOutlinedIcon sx={{fontSize: 18}}/>
                        </IconButton>
                    </InsightsTooltip>
                )}
            </Stack>
        </Box>
    );
}

const STAT_CARD_PAPER_SX = {
    width: "100%",
    minWidth: 0,
    borderRadius: 2,
    borderColor: "divider",
    bgcolor: "background.paper",
    boxSizing: "border-box"
} as const;

const STAT_CARD_PADDING = {px: 2, py: 1.5} as const;

function StatRowGap() {
    return <Box sx={{my: 0.75}}/>;
}

interface Props {
    data: StatCardData;
    loading?: InsightsLoading;
    /** Targets that arrive on their own request keep their own state, so the figure never waits on them. */
    targetLoading?: InsightsLoading;
}

export function StatCard({data, loading, targetLoading}: Props) {
    const {t} = useTranslation();
    const {title, hint, value, comparison, target} = data;

    const skeleton = loading === "skeleton";
    const dimmed = dimmedWhile(loading);
    const targetSkeleton = targetLoading === "skeleton";
    const targetDimmed = dimmedWhile(targetLoading ?? loading);
    const showTarget = target != null || targetSkeleton;

    const moved = comparison != null && comparison.trend !== "neutral" && comparison.change != null;
    const good = comparison != null && comparisonIsGood(comparison);

    const tone = target == null ? null : targetProgressTone(target);
    const progress = target == null ? 0 : Math.round(target.progress);
    const toTarget = target == null ? 0 : Math.min(Math.max(target.progress, 0), 100);
    const past = target == null ? 0 : Math.min(Math.max(target.progress - 100, 0), OVERSHOOT);

    const previous = comparison?.previous;

    const panel = previous == null && target == null ? null : (
        <React.Fragment>
            <TooltipTitle title={title}/>

            <TooltipRow label={t("Current")} value={value}/>

            {previous != null && <TooltipRow label={t("Compare period")} value={previous}/>}

            {comparison?.change != null && (
                <TooltipRow
                    label={t("Change")}
                    value={moved ? `${comparison.trend === "down" ? "−" : "+"}${comparison.change}` : comparison.change}
                    color={moved ? (good ? "success.main" : "error.main") : "text.secondary"}
                />
            )}

            {target != null && tone != null && (
                <TooltipRow label={target.label} value={`${progress}%`} color={`${tone}.main`}/>
            )}
        </React.Fragment>
    );

    const withPanel = (node: React.ReactElement) =>
        panel == null ? node : (
            <InsightsTooltip placement={"top"} title={panel}>
                {node}
            </InsightsTooltip>
        );

    return (
        <Paper
            variant={"outlined"}
            sx={{
                ...STAT_CARD_PAPER_SX,
                ...STAT_CARD_PADDING,
                height: "100%",
                display: "flex",
                flexDirection: "column"
            }}
        >
            <StatTitle title={title} hint={hint}/>

            <StatRowGap/>

            <Stack
                direction={"row"}
                alignItems={"center"}
                flexWrap={"wrap"}
                gap={1}
                sx={{minWidth: 0, minHeight: "32px", ...dimmed}}
            >
                {skeleton ? (
                    <Skeleton animation={"wave"} variant={"rounded"} width={80} height={32}/>
                ) : (
                    <React.Fragment>
                        <HeadlineValue value={value}/>
                        <TrendChip comparison={comparison} panel={panel ?? undefined}/>
                    </React.Fragment>
                )}
            </Stack>

            {showTarget && (
                <React.Fragment>
                    <Box sx={{my: 0.75}}/>

                    <Box
                        sx={{
                            mt: "auto",
                            minHeight: "34px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                            minWidth: 0,
                            ...targetDimmed
                        }}
                    >
                        {skeleton || targetSkeleton || target == null || tone == null ? (
                            <React.Fragment>
                                <Box sx={{display: "flex", gap: "3px"}}>
                                    <Skeleton animation={"wave"} variant={"rounded"} height={8}
                                              sx={{flexGrow: 100, flexBasis: 0}}/>
                                    <Skeleton animation={"wave"} variant={"rounded"} height={8}
                                              sx={{flexGrow: OVERSHOOT, flexBasis: 0}}/>
                                </Box>

                                <Box sx={{display: "flex", justifyContent: "space-between"}}>
                                    <Skeleton animation={"wave"} variant={"rounded"} width={56} height={14}/>
                                    <Skeleton animation={"wave"} variant={"rounded"} width={28} height={14}/>
                                </Box>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                {withPanel(
                                    <Box
                                        role={"progressbar"}
                                        aria-label={`${title}: ${target.label}`}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        aria-valuenow={progress}
                                        tabIndex={0}
                                        sx={{
                                            position: "relative",
                                            display: "flex",
                                            gap: "3px",
                                            cursor: "default",
                                            "&::before": {content: '""', position: "absolute", inset: "-6px 0"}
                                        }}
                                    >
                                        <Segment span={100} filled={toTarget} tone={tone}/>
                                        <Segment span={OVERSHOOT} filled={(past / OVERSHOOT) * 100} tone={tone}/>
                                    </Box>
                                )}

                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1
                                }}>
                                    <Typography variant={"body2"} color={"text.secondary"} noWrap>
                                        {target.label}
                                    </Typography>

                                    <Typography
                                        variant={"body2"}
                                        fontWeight={600}
                                        sx={{color: `${tone}.main`, flexShrink: 0, fontVariantNumeric: "tabular-nums"}}
                                    >
                                        {progress}%
                                    </Typography>
                                </Box>
                            </React.Fragment>
                        )}
                    </Box>
                </React.Fragment>
            )}
        </Paper>
    );
}
